import { Column } from "primereact/column";
import { TreeTable } from "primereact/treetable";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../services/redux/hooks/hooks";
import {
  handleformatforchild,
  handleNullAndZero,
} from "../../../../react-query/hooks/HCompliance";
import { formatNumberwithCommas } from "../../../../utils/formatter";
import { Whisper, Tooltip } from "rsuite";
import ProductInfoIcon from "../../../../assets/info-circle-grey.svg";

type Props = {
  data: Record<string, any>[];
  month: string;
  vendors: string[];
  partnerVendor: string;
  district: string[];
  isEstimated?: Record<string, number> | number;
  othersVendors?: string[];
};

const TreeTableComponent = ({
  data,
  month,
  vendors,
  partnerVendor,
  district,
  isEstimated = 0,
  othersVendors = [],
}: Props) => {
  const [expandedKeys, setExpandedKeys] = useState({});
  const [newData, setNewData] = useState<any[]>([]);

  const normalizeVendorName = (value: string) =>
    value.replace(/[^a-z0-9]/gi, "").toUpperCase();

  const estimatedVendorSet = new Set(
    Object.entries(typeof isEstimated === "object" ? isEstimated ?? {} : {})
      .filter(([, value]) => Number(value) === 1)
      .map(([key]) => normalizeVendorName(key))
  );

  const hasEstimatedVendor = (vendorName: string) => {
    if (typeof isEstimated === "number") {
      return isEstimated === 1;
    }

    return estimatedVendorSet.has(normalizeVendorName(vendorName));
  };

  useEffect(() => {
    if (data && vendors) {
      setNewData(
        data
          .map((mtdObj: any) => {
            Object.keys(mtdObj).map((key) => {
              if (
                mtdObj[key] === 0 ||
                mtdObj[key] === "0%" ||
                mtdObj[key] === "0.00%"
              ) {
                mtdObj[key] = "-";
              }
              if (Array.isArray(mtdObj[key])) {
                mtdObj[key] = handleNullAndZero(mtdObj[key]);
              }
              if (
                (mtdObj[key] === null || mtdObj[key] === "") &&
                key !== "GainsMonthlyForecast"
              ) {
                mtdObj[key] = "-";
              }
            });
            return {
              ...mtdObj,
            };
          })
          .map((mtdObj: any) => {
            if (mtdObj["key"] === "2") {
              const ele = vendors
                .map((vendor: string) => ({
                  [vendor]:
                    mtdObj[vendor] !== "-" && mtdObj[vendor][0] !== "$"
                      ? `$${mtdObj[vendor]}`
                      : mtdObj[vendor],
                }))
                .reduce((a: any, c: any) => ({ ...a, ...c }), {
                  GainsMonthlyForecast:
                    mtdObj["GainsMonthlyForecast"] !== null
                      ? !isNaN(mtdObj["GainsMonthlyForecast"])
                        ? `$${mtdObj["GainsMonthlyForecast"]}`
                        : mtdObj["GainsMonthlyForecast"][0] !== "$"
                        ? `$${mtdObj["GainsMonthlyForecast"]}`
                        : mtdObj["GainsMonthlyForecast"]
                      : null,
                  Total: !isNaN(mtdObj["Total"])
                    ? `$${mtdObj["Total"]}`
                    : mtdObj["Total"][0] !== "$"
                    ? `$${mtdObj["Total"]}`
                    : mtdObj["Total"],
                  key: mtdObj["key"],
                  name: mtdObj["name"],
                });

              return { ...ele };
            } else if (mtdObj["key"] === "0") {
              const ele = vendors
                .map((vendor: string) => ({
                  [vendor]: !isNaN(mtdObj[vendor])
                    ? formatNumberwithCommas(mtdObj[vendor])
                    : mtdObj[vendor],
                }))
                .reduce((a: any, c: any) => ({ ...a, ...c }), {
                  GainsMonthlyForecast:
                    mtdObj["GainsMonthlyForecast"] !== null
                      ? !isNaN(mtdObj["GainsMonthlyForecast"])
                        ? formatNumberwithCommas(mtdObj["GainsMonthlyForecast"])
                        : mtdObj["GainsMonthlyForecast"]
                      : null,
                  Total: !isNaN(mtdObj["Total"])
                    ? formatNumberwithCommas(mtdObj["Total"])
                    : mtdObj["Total"],
                  key: mtdObj["key"],
                  name: mtdObj["name"],
                  children: handleformatforchild(
                    [...mtdObj["children"]],
                    [...vendors]
                  ),
                });
              return { ...ele };
            } else {
              return { ...mtdObj };
            }
          })
      );
    }
  }, [data, vendors]);

  // Flatten the tree (rows + nested children) so we can inspect every value.
  const flattenNodes = (nodes: any[]): any[] =>
    (nodes ?? []).flatMap((n: any) => [n, ...flattenNodes(n?.children ?? [])]);


  const hasAvgValue = (vendor: string) => {
    const avgRow = newData.find((n: any) => n?.key === "2");
    const v = avgRow?.[vendor];
    return v !== undefined && v !== null && v !== "" && v !== "-";
  };

  // Keep only vendors that have real data in at least one row; drop columns
  // whose cells are all empty ("-", null, "").
  const visibleVendors = useMemo(() => {
    const nodes = flattenNodes(newData);
    return vendors.filter((vendor: string) =>
      nodes.some((n: any) => {
        const val = n?.[vendor];
        return (
          val !== undefined && val !== null && val !== "" && val !== "-"
        );
      })
    );
  }, [newData, vendors]);

  // Compute a compact width per column based on the longest of its header
  // text and its cell values (in ch), so columns fit their content and the
  // user scrolls less horizontally. Width is always >= content, which keeps
  // the separate scrollable header/body tables aligned.
  // Compute a compact width per column by measuring the actual rendered text
  // width of the header (bold) and every cell value (normal) with a canvas,
  // then taking the max. This gives true cut-to-cut widths with no guess
  // buffer, and since the same px width is applied to the header and body
  // cells their borders line up per column.
  const columnWidths = useMemo(() => {
    const nodes = flattenNodes(newData);

    const winH = typeof window !== "undefined" ? window.innerHeight : 800;
    const winW = typeof window !== "undefined" ? window.innerWidth : 1440;
    const fontPx = winH * 0.018; // header/body font-size is ~1.8vh
    const padPx = winW * 0.012; // cell padding: 0.6vw on each side
    const borderPx = 2; // 1px left + 1px right cell border

    const family =
      typeof window !== "undefined"
        ? getComputedStyle(document.body).fontFamily || "sans-serif"
        : "sans-serif";
    const canvas =
      typeof document !== "undefined"
        ? document.createElement("canvas")
        : null;
    const ctx = canvas ? canvas.getContext("2d") : null;

    const textWidth = (text: string, bold: boolean) => {
      if (!ctx) return String(text).length * fontPx * 0.56;
      ctx.font = `${bold ? 700 : 400} ${fontPx}px ${family}`;
      return ctx.measureText(String(text)).width;
    };

    const widthFor = (label: string, field: string, hasIcon = false) => {
      let contentPx = textWidth(label, true);
      if (hasIcon) contentPx += winH * 0.015 + 4; // info icon + gap
      nodes.forEach((n: any) => {
        const v = n?.[field];
        if (v !== undefined && v !== null && v !== "") {
          const w = textWidth(String(v), false);
          if (w > contentPx) contentPx = w;
        }
      });
      return `${Math.ceil(contentPx + padPx + borderPx)}px`;
    };

    const map: Record<string, string> = {};
    visibleVendors.forEach((vendor: string) => {
      const hasIcon =
        hasEstimatedVendor(vendor) || vendor.trim().toLowerCase() === "others";
      map[vendor] = widthFor(vendor, vendor, hasIcon);
    });
    map.Total = widthFor("Total", "Total");
    map.GainsMonthlyForecast = widthFor(
      "GAINS Monthly Forecast",
      "GainsMonthlyForecast"
    );
    return map;
  }, [newData, visibleVendors, othersVendors]);

  return (
    <div className="allocation-compliance-table">
      <TreeTable
        value={newData ?? []}
        expandedKeys={expandedKeys}
        onToggle={(e) => setExpandedKeys(e.value)}
        scrollHeight="22vh"
        scrollable
        frozenWidth={`${
          vendors.length === 2 || vendors.length === 3 ? "20vw" : "20vw"
        }`}
        className="mt-5"
        style={{
          // maxHeight: "auto",
          // overflow: "auto",
          height: "100%",
          minWidth: "100%",
        }}
      >
        <Column
          field="name"
          header={() => <div className="text-white">Vendor</div>}
          frozen
          style={{ width: "20vw" }}
          headerStyle={{ width: "20vw" }}
          expander
          body={(node) => {
            return (
              <span className="font-bold">
                {node?.key === "0" && (
                  <span className="text-[#86BC25]">{month} </span>
                )}
                <span>{node?.name}</span>
                {node?.key === "0" && (
                  <span className="text-[#86BC25]">
                    {" "}
                    {partnerVendor
                      ? `for ${partnerVendor}`
                      : district.length === 1
                      ? `for ${district[0]}`
                      : district.length === 0
                      ? `for All Districts`
                      : `for Selected Districts`}
                  </span>
                )}
              </span>
            );
          }}
        />
        {visibleVendors.map((ele, i) => (
          <Column
            key={ele}
            field={ele}
            header={() => (
              <div className="inline-flex items-center gap-1 whitespace-nowrap max-w-full">
                <span>{ele}</span>
                {hasEstimatedVendor(ele) && hasAvgValue(ele) && (
                  <Whisper
                    placement="top"
                    speaker={
                      <Tooltip>
                        <span className="text-xs">Benchmark SKU Cost estimated using quote</span>
                      </Tooltip>
                    }
                  >
                    <img
                      src={ProductInfoIcon}
                      alt="info"
                      className="w-[1.5vh] cursor-pointer inline shrink-0"
                    />
                  </Whisper>
                )}
                {ele.trim().toLowerCase() === "others" && (
                  <Whisper
                    placement="top"
                    speaker={
                      <Tooltip>
                        <div className="text-xs text-left">
                          {othersVendors.length > 0 ? (
                            <ul className="list-disc pl-3">
                              {othersVendors.map((vendor) => (
                                <li key={vendor}>{vendor}</li>
                              ))}
                            </ul>
                          ) : (
                            <div>No vendors available</div>
                          )}
                        </div>
                      </Tooltip>
                    }
                  >
                    <span className="inline-flex items-center cursor-pointer shrink-0">
                      <img
                        src={ProductInfoIcon}
                        alt="info"
                        className="w-[1.5vh] cursor-pointer inline shrink-0"
                      />
                    </span>
                  </Whisper>
                )}
              </div>
            )}
            style={{ width: columnWidths[ele], whiteSpace: "nowrap" }}
            headerStyle={{ width: columnWidths[ele], whiteSpace: "nowrap" }}
            body={(node) => {
              return <span>{node[ele]}</span>;
            }}
          />
        ))}
        <Column
          field="total"
          header="Total"
          style={{ width: columnWidths.Total, whiteSpace: "nowrap" }}
          headerStyle={{ width: columnWidths.Total, whiteSpace: "nowrap" }}
          body={(node) => {
            return <span>{node?.Total}</span>;
          }}
        />
        <Column
          field="GainsMonthlyForecast"
          header="GAINS Monthly Forecast"
          style={{
            width: columnWidths.GainsMonthlyForecast,
            whiteSpace: "nowrap",
          }}
          headerStyle={{
            width: columnWidths.GainsMonthlyForecast,
            whiteSpace: "nowrap",
          }}
          body={(node) => {
            return <span>{node?.GainsMonthlyForecast}</span>;
          }}
        />
      </TreeTable>
    </div>
  );
};

export default TreeTableComponent;
